import { ALL_LOCATIONS } from '@solinkcorp/vd-common';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import useLocale from 'src/hooks/useLocale';
import CommonUtil from 'utils/CommonUtil';
import LocaleUtil from 'utils/LocaleUtil';
import { type VideoDiscoverySite } from 'utils/types';
import { getLocationInfoToDisplay } from './locationSelectorUtils';
import styles from './SelectedLocations.module.sass';

interface SelectedLocationsProps {
  locationsList: readonly VideoDiscoverySite[];
  locationIds?: readonly string[];
  locationTags?: readonly string[];
  loading?: boolean;
  noLocationAccess?: boolean;
  maxCharacterLimit: number;
  tagClassName?: string;
}

const SelectedLocations: React.FC<SelectedLocationsProps> = (props) => {
  const {
    locationIds,
    locationTags,
    locationsList,
    loading = false,
    noLocationAccess = false,
    maxCharacterLimit,
    tagClassName,
  } = props;
  const locale = useLocale();

  const processedLocationNames = useMemo(() => {
    if (loading) {
      return locale.general.loading;
    }

    if (noLocationAccess) {
      return locale.general.noLocations;
    }

    if (locationIds) {
      if (locationIds.length > 1) {
        return `${locationIds.length} ${locale.general.locations}`;
      }
      const selectedLocation = locationIds[0]
        ? locationsList.find((location) => location.id === locationIds[0])
        : locationsList.find((location) => location.id === ALL_LOCATIONS);

      return selectedLocation?.name ?? `${locationIds[0]}`;
    }

    return '';
  }, [loading, noLocationAccess, locationIds, locale, locationsList]);

  const sortedLocationTags = [...(locationTags || [])].sort(
    CommonUtil.sortAlphabeticallyAndNumerically,
  );

  const truncatedLocationTagsString = `${LocaleUtil.getLocalizedStr({
    string:
      sortedLocationTags.length > 1
        ? locale.locationTagging.xLocationTags
        : locale.locationTagging.xLocationTag,
    replacements: { count: sortedLocationTags.length },
  })}`;
  const { locationTagsToDisplay, locationsToDisplay } = getLocationInfoToDisplay({
    locationNames: processedLocationNames,
    locationTags: sortedLocationTags,
    maxCharacterLimit,
    truncatedLocationTagsString,
  });

  const locationTagClass = classNames(tagClassName, styles.locationTagPill);

  return (
    <div>
      <span className={styles.locationLabels}>{locationsToDisplay}</span>
      {!!locationTagsToDisplay?.length && !loading && (
        <span className={styles.locationTabLabelsWrapper}>
          {locationTagsToDisplay?.map((tag) => (
            <span className={locationTagClass} key={tag}>
              {tag}
            </span>
          ))}
        </span>
      )}
    </div>
  );
};

export default SelectedLocations;
